/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Button, RichTextEditor } from '@/components/ui';
import Input from '@/components/ui/Input/Input';
import { WebsitePageSection } from '@/types';
import { showToast } from '@/lib/toast';
import { ContentWithListFormDataSchema } from '@/components/Modals/EditContentsModals/EditContentsSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import TrashIcon from '@/assets/icons/TrashIcon';

interface InlineListEditorProps {
  data: WebsitePageSection;
  onSave: (updatedData: Partial<WebsitePageSection>) => void;
  onCancel: () => void;
}

// Form values type - listOfPurpose is always an array in the form (never null)
type FormValues = {
  sectionTitle: string;
  description: string | null;
  listOfPurpose: string[];
};

const InlineListEditor: React.FC<InlineListEditorProps> = ({ data, onSave, onCancel }) => {
  const [isLoading, setIsLoading] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset
  } = useForm<FormValues>({
    resolver: zodResolver(ContentWithListFormDataSchema),
    defaultValues: {
      sectionTitle: data.sectionTitle ?? '',
      description: data.description ?? '',
      listOfPurpose: data.listOfPurpose ?? ['']
    },
    mode: 'all'
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'listOfPurpose' as const
  });

  useEffect(() => {
    const listItems = data.listOfPurpose && data.listOfPurpose.length > 0 
      ? data.listOfPurpose 
      : [''];
    reset({
      sectionTitle: data.sectionTitle ?? '',
      description: data.description ?? '',
      listOfPurpose: listItems
    });
  }, [data, reset]);

  const onSubmit = async (formData: FormValues) => {
    if (!data.sectionTag) {
      showToast.error('Section tag not found.');
      return;
    }

    try {
      setIsLoading(true);
      
      // Filter out empty strings from the list
      const listItems = formData.listOfPurpose ?? [];
      const filteredList = listItems.filter(item => item.trim() !== '');

      onSave({
        sectionTitle: formData.sectionTitle,
        description: formData.description ?? '',
        listOfPurpose: filteredList.length > 0 ? filteredList : null,
        contentType: 'WITH_LIST'
      });
    } catch (error) {
      setIsLoading(false);
      showToast.error('An unexpected error occurred.');
      console.error('Update failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Controller
          name="sectionTitle"
          control={control}
          render={({ field }) => (
            <Input
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder="Section Title"
              label="Section Title"
              error={errors.sectionTitle?.message}
              $dataId="inline-list-section-title-input"
              $bgColor="var(--inputBgColor)"
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
             <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                        <RichTextEditor
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          placeholder="Description"
                          disabled={isLoading}
                            extendedToolbar={{
                            textStyle: false,
                            fontSize: false,
                            image: false
                          }} 
                          toolbarContainerClassName="bg-[var(--inputBgColor)]"
                          contentAreaClassName="bg-[var(--inputBgColor)] min-h-[10rem] text-text-primary whitespace-pre-wrap"
                        />
                        {errors.description?.message && (
                          <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                        )}
                      </div>
          )}
        />

        {/* List Items */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">List Items</label>
          {fields.map((field, index) => (
            <div key={field.id} className="flex gap-2 items-start">
              <Controller
                name={`listOfPurpose.${index}` as const}
                control={control}
                render={({ field: listField }) => (
                  <Input
                    value={listField.value ?? ''}
                    onChange={listField.onChange}
                    onBlur={listField.onBlur}
                    placeholder={`List item ${index + 1}`}
                    error={errors.listOfPurpose?.[index]?.message}
                    $dataId={`inline-list-item-${index}-input`}
                    className="flex-1"
                    $bgColor="var(--inputBgColor)"
                  />
                )}
              />
              {fields.length > 1 && (
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => remove(index)}
                  className="mt-0"
                >
                  <TrashIcon className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => append('')}
          >
            Add Item
          </Button>
          {errors.listOfPurpose && (
            <p className="text-sm text-red-600">{errors.listOfPurpose.message}</p>
          )}
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={isLoading}
          disabled={isLoading}
        >
          Save
        </Button>
      </div>
    </form>
  );
};

export default InlineListEditor;

